<template>
  <ContactReviewDisplayComponent
    :accountData="accountData"
    :companyIndex="companyIndex"
    :editableAccountFields="accountFields"
    :editableCompanyFields="companyFields"
    :enableAutofill="false"
    :existingCompanies="[]"
    :company_section_title="$t('signup.lt.extra_info.title')"
    :company_section_description="$t('signup.lt.extra_info.description')"
    @updateAccountData="$emit('updateAccountData', $event)"
    @handleUpdateCompany="$emit('handleUpdateCompany', $event)"
    @selectExistingCompany="$emit('selectExistingCompany', $event)"
    @resetCompanySelection="$emit('resetCompanySelection')"
  />
</template>

<script setup lang="ts">
import { useCompanyFinder } from "@/composables/useCompanyFinder";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { isValidIban, useValidators } from "@/composables/useValidators";
import ContactReviewDisplayComponent from "@/signup/ContactReviewDisplayComponent.vue";
import type { Account, Company, ContactFieldType } from "@/types";
import { computed, watch } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  accountData: Account;
  companyIndex?: number;
  userType: "private" | "company" | undefined;
}>();

const emit = defineEmits<{
  updateAccountData: [accountData: Account];
  handleUpdateCompany: [company: Company];
  selectExistingCompany: [companyId: number];
  resetCompanySelection: [];
}>();

const { t } = useI18n();
const { required, iban } = useValidators(t);

const { selectedCompany } = useCompanyFinder(props.accountData);
const company = selectedCompany.value;

const accountFields: ContactFieldType[] = [];

const companyFields = computed(() => {
  const fields: ContactFieldType[] = [];

  // Only include VAT field if userType is not private
  if (props.userType !== "private") {
    fields.push({
      label: t("company.vat"),
      id: "company-vat",
      model: "vat",
      type: "text",
      editable: true,
      validators: { required },
    });
  }

  // Always include bank fields
  fields.push(
    {
      label: t("company.bank_account"),
      id: "company-bank-account",
      model: "bank_account",
      type: "text",
      editable: true,
      validators: [required, iban],
    },
    {
      label: t("company.bank_name"),
      id: "company-company-bank-name",
      model: "bank_name",
      type: "text",
      editable: true,
      validators: { required },
    },
    {
      label: t("company.bank_swift"),
      id: "company-company-bank-swift",
      model: "bank_swift",
      type: "text",
      editable: true,
      validators: { required },
    }
  );

  return fields;
});

watch(
  () => company.bank_account,
  async (newValue) => {
    if (newValue && isValidIban(newValue)) {
      try {
        const response = await goSolidApi.get(
          `/api/utils/iban-validator/${newValue}`
        );
        if (
          response &&
          response.status === 200 &&
          response.data &&
          response.data.valid
        ) {
          const update_data = {
            ...company,
            bank_name: response.data.bank_name,
            bank_swift: response.data.swift_code,
          } as Company;
          emit("handleUpdateCompany", update_data);
        }
      } catch {
        // Fail silently if we can't fetch the bank name and swift code
      }
    }
  }
);
</script>

<style scoped lang="scss"></style>
